import { useState, forwardRef } from "react";
import { m } from "framer-motion";
import { toast } from "sonner";
import { submitLead } from "../lib/api";
import { track } from "../lib/analytics";
import { AGENT } from "../data/site";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const INTERESTS = ["ACA", "Dental & Vision", "Supplemental", "Not sure yet"];

const Contact = forwardRef((props, ref) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interests: ["ACA"], message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consentError, setConsentError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleInterest = (opt) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(opt)
        ? f.interests.filter((i) => i !== opt)
        : [...f.interests, opt],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone so I can reach you.");
      return;
    }
    if (!form.consent) {
      setConsentError("Please confirm your consent to continue.");
      return;
    }
    setConsentError("");
    setLoading(true);
    try {
      const { interests, ...rest } = form;
      await submitLead({ ...rest, interest: interests.join(", ") || "Not sure yet" });
      track("quote_form_submit", { interest: interests.join(", ") || "Not sure yet" });
      setDone(true);
      toast.success(`Got it! ${AGENT.firstName} will reach out within one business day.`);
      setForm({ name: "", email: "", phone: "", interests: ["ACA"], message: "", consent: false });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        err.response?.status === 429 && typeof detail === "string"
          ? detail
          : "Something went wrong. Please call me directly and I'll help right away."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-24 md:py-32 bg-[#1E3A2F]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div className="text-[#F9F6F0]">
          <p className="text-sm uppercase tracking-[0.25em] font-bold text-[#E8A365] mb-4">Free & no pressure</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            Let's find your right plan together
          </h2>
          <p className="mt-6 text-[#F9F6F0]/80 leading-relaxed text-lg max-w-md">
            Tell me a little about what you need. I'll review your options and follow up within one business
            day, usually the same day. There's never a cost or an obligation.
          </p>
          <div className="mt-10 space-y-3 text-[#F9F6F0]/90">
            <p className="font-display text-2xl">{AGENT.phone}</p>
            <p>{AGENT.email}</p>
            <p className="text-[#F9F6F0]/60 text-sm">{AGENT.location}</p>
          </div>
        </div>

        <m.form
          onSubmit={onSubmit}
          data-testid="lead-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-[#F9F6F0] rounded-3xl p-8 md:p-10 shadow-[0_24px_60px_rgba(0,0,0,0.25)]"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#1A1714]">Full name</label>
              <Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="mt-2 bg-white border-[#D8D0C5] focus-visible:ring-[#C85A40]" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1A1714]">Phone</label>
              <Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="mt-2 bg-white border-[#D8D0C5] focus-visible:ring-[#C85A40]" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-[#1A1714]">Email</label>
            <Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="mt-2 bg-white border-[#D8D0C5] focus-visible:ring-[#C85A40]" />
          </div>

          <div className="mt-5">
            <label className="text-sm font-semibold text-[#1A1714]">I'm interested in <span className="font-normal text-[#4A453F]">(select all that apply)</span></label>
            <div className="mt-2 flex flex-wrap gap-2">
              {INTERESTS.map((opt) => {
                const active = form.interests.includes(opt);
                return (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`interest-${opt}`}
                    aria-pressed={active}
                    onClick={() => toggleInterest(opt)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-200 ${
                      active
                        ? "bg-[#C85A40] text-white border-[#C85A40]"
                        : "bg-white text-[#4A453F] border-[#D8D0C5] hover:border-[#C85A40]"
                    }`}
                  >
                    {active && <Check className="w-3.5 h-3.5" />}
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <label className="text-sm font-semibold text-[#1A1714]">Anything else? (optional)</label>
            <Textarea data-testid="lead-message" value={form.message} onChange={set("message")} placeholder="Tell me about your household, doctors, or budget..." rows={3} className="mt-2 bg-white border-[#D8D0C5] focus-visible:ring-[#C85A40]" />
          </div>

          <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="lead-consent-label">
            <input
              type="checkbox"
              data-testid="lead-consent"
              checked={form.consent}
              onChange={(e) => {
                setForm((f) => ({ ...f, consent: e.target.checked }));
                if (e.target.checked) setConsentError("");
              }}
              aria-invalid={!!consentError}
              className="mt-1 h-4 w-4 flex-none rounded border-[#D8D0C5] accent-[#C85A40]"
            />
            <span className="text-sm text-[#4A453F] leading-relaxed">
              By submitting this form, I consent to being contacted by Coverage with Jacqueline regarding insurance
              plan options. I understand my information will not be sold or shared with third parties.
            </span>
          </label>
          {consentError && (
            <p role="alert" data-testid="lead-consent-error" className="mt-2 text-sm font-medium text-[#C85A40]">
              {consentError}
            </p>
          )}

          <button
            type="submit"
            data-testid="lead-submit"
            disabled={loading || done}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A40] px-8 py-4 text-base font-semibold text-[#F9F6F0] hover:bg-[#A64730] active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-70"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : done ? <Check className="w-5 h-5" /> : null}
            {done ? "Request received" : loading ? "Sending..." : "Request my free quote"}
          </button>
          <p className="mt-3 text-center text-xs text-[#4A453F]">
            No spam, ever. See our{" "}
            <Link to="/privacy" data-testid="contact-privacy-link" className="underline hover:text-[#C85A40]">Privacy Policy</Link>.
          </p>
        </m.form>
      </div>
    </section>
  );
});

export default Contact;
